// apps/provider/src/services/api/veradigmPM.js
// Veradigm Practice Management billing integration methods.
//
// Methods: getVeradigmPMConfig, saveVeradigmPMConfig, updateVeradigmPMConfig,
//          testVeradigmPMConnection, syncVeradigmPMReferenceData,
//          getVeradigmPMMappings, updateVeradigmPMMapping,
//          searchVeradigmPMPatients, linkVeradigmPMPatient,
//          postChargesToVeradigmPM, verifyVeradigmPMCharges, retryVeradigmPMCharges
//
// Response types: @prism-ehr/shared → VeradigmPMConfig, VeradigmPMMapping,
//                 VeradigmPMChargeResult, VeradigmPMVerifyResult,
//                 VeradigmPMTestConnectionResult, VeradigmPMSyncResult,
//                 VeradigmPMPatientSearchResult

/**
 * @returns {Object} Veradigm PM domain methods (bound to `this` of the composed apiService)
 */
export function createVeradigmPMMethods() {
  return {

    // ============ Configuration ============

    /**
     * Get Veradigm PM config for the current org.
     * Passwords are masked in the response.
     *
     * Xano: GET /veradigm-pm-config
     *
     * @returns {Promise<import('@prism-ehr/shared').VeradigmPMConfig | null>}
     */
    async getVeradigmPMConfig() {
      return this.request('/veradigm-pm-config', {
        method: 'GET'
      });
    },

    /**
     * Create initial Veradigm PM config.
     *
     * Xano: POST /veradigm-pm-config
     *
     * @param {Object} config
     * @returns {Promise<import('@prism-ehr/shared').VeradigmPMConfig>}
     */
    async saveVeradigmPMConfig(config) {
      return this.request('/veradigm-pm-config', {
        method: 'POST',
        body: JSON.stringify(config)
      });
    },

    /**
     * Update existing Veradigm PM config.
     *
     * Xano: PATCH /veradigm-pm-config
     *
     * @param {Object} config
     * @returns {Promise<import('@prism-ehr/shared').VeradigmPMConfig>}
     */
    async updateVeradigmPMConfig(config) {
      return this.request('/veradigm-pm-config', {
        method: 'PATCH',
        body: JSON.stringify(config)
      });
    },

    // ============ Connection Test ============

    /**
     * Test Veradigm PM credentials by calling GetToken + GetUserAuthentication + Echo.
     *
     * Xano: POST /veradigm-pm-test-connection
     *
     * @returns {Promise<import('@prism-ehr/shared').VeradigmPMTestConnectionResult>}
     */
    async testVeradigmPMConnection() {
      return this.request('/veradigm-pm-test-connection', {
        method: 'POST',
        body: JSON.stringify({})
      });
    },

    // ============ Reference Data ============

    /**
     * Pull reference data from PM and store in mapping table.
     *
     * Xano: POST /veradigm-pm-sync-reference-data
     *
     * @param {string[]} types - Array of: 'practitioners', 'locations', 'departments', 'pos', 'coverage_types'
     * @returns {Promise<import('@prism-ehr/shared').VeradigmPMSyncResult>}
     */
    async syncVeradigmPMReferenceData(types) {
      return this.request('/veradigm-pm-sync-reference-data', {
        method: 'POST',
        timeoutMs: 180000,
        body: JSON.stringify({ types })
      });
    },

    /**
     * Get cached PM reference data mappings.
     *
     * Xano: GET /veradigm-pm-mappings
     *
     * @param {string} [mappingType] - Optional filter: 'practitioner', 'location', 'department', 'pos', 'coverage_type'
     * @returns {Promise<import('@prism-ehr/shared').VeradigmPMMapping[]>}
     */
    async getVeradigmPMMappings(mappingType) {
      const params = mappingType ? `?mapping_type=${mappingType}` : '';
      return this.request(`/veradigm-pm-mappings${params}`, {
        method: 'GET'
      });
    },

    /**
     * Update a PM mapping (manual match to Prism entity).
     *
     * Xano: PATCH /veradigm-pm-mappings/{id}
     *
     * @param {string} mappingId
     * @param {Object} data - { prism_id, prism_label }
     * @returns {Promise<import('@prism-ehr/shared').VeradigmPMMapping>}
     */
    async updateVeradigmPMMapping(mappingId, data) {
      return this.request(`/veradigm-pm-mappings/${mappingId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    // ============ Patient Linking ============

    /**
     * Search PM for patients by name/DOB.
     *
     * Xano: POST /veradigm-pm-search-patients
     *
     * @param {Object} searchParams - { first_name, last_name, dob? }
     * @returns {Promise<import('@prism-ehr/shared').VeradigmPMPatientSearchResult[]>}
     */
    async searchVeradigmPMPatients(searchParams) {
      return this.request('/veradigm-pm-search-patients', {
        method: 'POST',
        body: JSON.stringify(searchParams)
      });
    },

    /**
     * Link a Prism patient to a PM patient ID.
     *
     * Xano: POST /veradigm-pm-link-patient
     *
     * @param {string} patientId - Prism patient ID
     * @param {string} pmPatientId - Veradigm PM internal patient ID
     * @returns {Promise<{success: boolean}>}
     */
    async linkVeradigmPMPatient(patientId, pmPatientId) {
      return this.request('/veradigm-pm-link-patient', {
        method: 'POST',
        body: JSON.stringify({ patient_id: patientId, pm_patient_id: pmPatientId })
      });
    },

    // ============ Charge Submission ============

    /**
     * Post charges from a Prism encounter to Veradigm PM via SaveChargeVoucher.
     *
     * Xano: POST /veradigm-pm-post-charges
     *
     * @param {string} encounterId
     * @param {string} [insuranceClaimId]
     * @param {boolean} [finalizeEncounter] - Fresh-billing flow only: on success stamps encounter.insurance_claim_id; on total failure deletes the never-linked claim so the encounter stays in Ready to Bill
     * @param {string} [policyCoverage] - Veradigm PolicyCoverage code for the charge: M medical, U self-pay, W workers comp, V motor vehicle. Determined per encounter by the biller in the review modal; the endpoint falls back to M when absent.
     * @returns {Promise<import('@prism-ehr/shared').VeradigmPMChargeResult>}
     */
    async postChargesToVeradigmPM(encounterId, insuranceClaimId, finalizeEncounter, policyCoverage) {
      const body = { encounter_id: encounterId };
      if (insuranceClaimId) body.insurance_claim_id = insuranceClaimId;
      if (finalizeEncounter) body.finalize_encounter = true;
      if (policyCoverage) body.policy_coverage = policyCoverage;
      return this.request('/veradigm-pm-post-charges', {
        method: 'POST',
        timeoutMs: 180000,
        body: JSON.stringify(body)
      });
    },

    /**
     * Verify charges landed in PM via GetServicesByVoucherID.
     * Verification is voucher-scoped server-side; pass voucher_id when known
     * (the backend falls back to the claim's charge log rows otherwise).
     *
     * Xano: POST /veradigm-pm-verify-charges
     *
     * @param {string} insuranceClaimId
     * @param {string} [voucherId]
     * @returns {Promise<import('@prism-ehr/shared').VeradigmPMVerifyResult>}
     */
    async verifyVeradigmPMCharges(insuranceClaimId, voucherId) {
      const body = { insurance_claim_id: insuranceClaimId };
      if (voucherId) body.voucher_id = voucherId;
      return this.request('/veradigm-pm-verify-charges', {
        method: 'POST',
        body: JSON.stringify(body)
      });
    },

    /**
     * Retry failed charge submissions.
     *
     * Xano: POST /veradigm-pm-retry-charges
     *
     * @param {string[]} [chargeLogIds] - Specific charge log IDs to retry
     * @param {string} [insuranceClaimId] - Retry all failed for this claim
     * @returns {Promise<import('@prism-ehr/shared').VeradigmPMChargeResult>}
     */
    async retryVeradigmPMCharges(chargeLogIds, insuranceClaimId) {
      const body = {};
      if (chargeLogIds) body.charge_log_ids = chargeLogIds;
      if (insuranceClaimId) body.insurance_claim_id = insuranceClaimId;
      return this.request('/veradigm-pm-retry-charges', {
        method: 'POST',
        timeoutMs: 180000,
        body: JSON.stringify(body)
      });
    },

    // ============ Per-User PM Credentials ============

    /**
     * Get current user's PM credentials (password masked).
     *
     * Xano: GET /veradigm-pm-user-config
     *
     * @returns {Promise<import('@prism-ehr/shared').VeradigmPMUserConfig | null>}
     */
    async getVeradigmPMUserConfig() {
      return this.request('/veradigm-pm-user-config', {
        method: 'GET'
      });
    },

    /**
     * Save current user's PM credentials (create or update).
     *
     * Xano: POST /veradigm-pm-user-config
     *
     * @param {Object} credentials - { pm_username, pm_password }
     * @returns {Promise<{success: boolean}>}
     */
    async saveVeradigmPMUserConfig(credentials) {
      return this.request('/veradigm-pm-user-config', {
        method: 'POST',
        body: JSON.stringify(credentials)
      });
    },

    /**
     * Delete current user's PM credentials (fall back to org default).
     *
     * Xano: DELETE /veradigm-pm-user-config
     *
     * @returns {Promise<{success: boolean}>}
     */
    async deleteVeradigmPMUserConfig() {
      return this.request('/veradigm-pm-user-config', {
        method: 'DELETE'
      });
    },

    // ============ Patient Sync ============

    /**
     * Manually trigger patient sync from PM for the current org.
     *
     * Xano: POST /veradigm-pm-trigger-patient-sync
     *
     * @returns {Promise<{success: boolean, patients_found: number, patients_linked: number, patients_created: number}>}
     */
    async triggerVeradigmPMPatientSync() {
      return this.request('/veradigm-pm-trigger-patient-sync', {
        method: 'POST',
        timeoutMs: 180000,
        body: JSON.stringify({})
      });
    },

    // ============ Payment Posting ============

    /**
     * Post a copay/payment to Veradigm PM.
     * Uses SaveVoucherPayment for self-pay or SavePaymentTransaction for insured.
     *
     * Xano: POST /veradigm-pm-post-payment
     *
     * @param {string} encounterId
     * @param {string} amount
     * @param {string} paymentMethod - cash | card | check
     * @param {string} reference - Transaction ID, check #, receipt #
     * @returns {Promise<{success: boolean, payment_id?: string, action_used: string}>}
     */
    async postPaymentToVeradigmPM(encounterId, amount, paymentMethod, reference) {
      return this.request('/veradigm-pm-post-payment', {
        method: 'POST',
        timeoutMs: 180000,
        body: JSON.stringify({
          encounter_id: encounterId,
          amount,
          payment_method: paymentMethod,
          reference,
        })
      });
    },

    // ============ Charge Log Listing ============

    /**
     * List charge log records for the current org with optional filters.
     *
     * Xano: GET /veradigm-pm-charge-logs
     *
     * @param {Object} [filters]
     * @param {string} [filters.status] - pending | submitted | verified | failed | retry
     * @param {string} [filters.patient_id]
     * @param {string} [filters.encounter_id]
     * @param {number} [filters.page]
     * @param {number} [filters.limit]
     * @returns {Promise<{items: import('@prism-ehr/shared').VeradigmPMChargeLog[], itemsTotal: number, pageTotal: number}>}
     */
    async getVeradigmPMChargeLogs(filters = {}) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
        }
      });
      const query = params.toString();
      return this.request(`/veradigm-pm-charge-logs${query ? `?${query}` : ''}`, {
        method: 'GET'
      });
    },

    // ============ Batch Tracking ============

    /**
     * List PM batches for the current org within a date range, with per-poster
     * charge aggregates keyed by pm_batch_number.
     *
     * Xano: GET /veradigm-pm-batches
     *
     * @param {Object} [filters]
     * @param {string} [filters.start_date] - YYYY-MM-DD (ET), default 14 days back
     * @param {string} [filters.end_date] - YYYY-MM-DD (ET), default 14 days ahead
     * @returns {Promise<{items: import('@prism-ehr/shared').VeradigmPMBatch[], charge_totals: import('@prism-ehr/shared').VeradigmPMBatchChargeTotal[]}>}
     */
    async getVeradigmPMBatches(filters = {}) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });
      const query = params.toString();
      return this.request(`/veradigm-pm-batches${query ? `?${query}` : ''}`, {
        method: 'GET'
      });
    },

    /**
     * Set or clear the scheduled poster for a batch. Pass '' to clear.
     *
     * Xano: POST /veradigm-pm-batch-assign
     */
    async assignVeradigmPMBatch(batchId, userId) {
      return this.request('/veradigm-pm-batch-assign', {
        method: 'POST',
        body: JSON.stringify({ batch_id: batchId, user_id: userId || '' })
      });
    },

    /**
     * Toggle the caller's "edits complete" mark on a batch (posters only).
     *
     * Xano: POST /veradigm-pm-batch-complete
     */
    async completeVeradigmPMBatch(batchId, completed) {
      return this.request('/veradigm-pm-batch-complete', {
        method: 'POST',
        body: JSON.stringify({ batch_id: batchId, completed: !!completed })
      });
    },

    /**
     * Re-sync a batch's PM status/identity from GetBatchDetail.
     *
     * Xano: POST /veradigm-pm-batch-refresh
     */
    async refreshVeradigmPMBatch(batchId) {
      return this.request('/veradigm-pm-batch-refresh', {
        method: 'POST',
        body: JSON.stringify({ batch_id: batchId })
      });
    },

    // ============ PM Data Read ============

    async getVeradigmPMAPILogs() {
      return this.request('/veradigm-pm-api-logs', { method: 'GET' });
    },

    async getVeradigmPMBatchDetail(batchId) {
      return this.request(`/veradigm-pm-batch-detail?batch_id=${batchId}`, { method: 'GET' });
    },

    async getVeradigmPMPatientPolicy(patientId) {
      return this.request(`/veradigm-pm-patient-policy?patient_id=${patientId}`, { method: 'GET' });
    },

    async getVeradigmPMBenefitPlan(patientId) {
      return this.request(`/veradigm-pm-benefit-plan?patient_id=${patientId}`, { method: 'GET' });
    },

    async getVeradigmPMPatientVouchers(patientId) {
      return this.request(`/veradigm-pm-patient-vouchers?patient_id=${patientId}`, { method: 'GET' });
    },

    async getVeradigmPMPatientPayments(patientId) {
      return this.request(`/veradigm-pm-patient-payments?patient_id=${patientId}`, { method: 'GET' });
    },

    async getVeradigmPMPatientDiagnoses(patientId) {
      return this.request(`/veradigm-pm-patient-diagnoses?patient_id=${patientId}`, { method: 'GET' });
    },

    // ============ Payment feed (phase 1, admin-only; used by scripts, no page) ============

    /**
     * Admin probe over the Veradigm PM payment store.
     * @param {Object} params - organization_id, mode (summary|patient|batch|reclassify), start_date, end_date, patient_id, pm_batch_id, dry_run
     */
    async getVeradigmPMPaymentsProbe(params) {
      const qs = new URLSearchParams();
      Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, String(v)); });
      return this.request(`/admin/veradigm-pm-payments-probe?${qs.toString()}`, { method: 'GET' });
    },

    /**
     * Paged operator backfill of the Veradigm PM payment store (<= 7 location-days per call).
     * @param {Object} params - organization_id, location_abbrev?, days?, dry_run?
     */
    async runVeradigmPMPaymentsBackfill(params) {
      return this.request('/admin/veradigm-pm-payments-backfill', {
        method: 'POST',
        body: JSON.stringify(params || {})
      });
    },

    // ============ PM Write-back (manual/optional) ============

    async syncPatientPolicyToVeradigmPM(patientId, policyData) {
      return this.request('/veradigm-pm-save-patient-policy', {
        method: 'POST',
        body: JSON.stringify({ patient_id: patientId, ...policyData })
      });
    },

    async syncPatientToVeradigmPM(patientId) {
      return this.request('/veradigm-pm-save-patient', {
        method: 'POST',
        body: JSON.stringify({ patient_id: patientId })
      });
    },

    // ============ Server Info & Required Fields ============

    async getVeradigmPMServerInfo() {
      return this.request('/veradigm-pm-server-info', { method: 'GET' });
    },

    async getVeradigmPMRequiredFields(actionName) {
      return this.request(`/veradigm-pm-required-fields?action_name=${actionName}`, { method: 'GET' });
    },

    /**
     * Sync a patient's demographics and insurance from Veradigm PM.
     * @param {Object} params
     * @param {string} params.patient_id
     * @param {string} [params.first_name]
     * @param {string} [params.last_name]
     * @param {string} [params.birthday]
     * @returns {Promise<{success: boolean}>}
     */
    async syncVeradigmDemographicsAndInsurance(params) {
      return this.request('/veradigm-sync-demographics-and-insurance', {
        method: 'POST',
        body: JSON.stringify(params),
      });
    },
  };
}
